/**
 * PurchaseOrdersPage — COM-02: Gestión de Órdenes de Compra
 */
import { useState } from 'react'
import {
  ClipboardList, Plus, Loader2, CheckCircle, Send,
  PackageCheck, XCircle, FileText, UserCheck, ArrowRight,
} from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { usePermission } from '@/hooks/usePermission'
import {
  usePurchaseOrders, useCreatePurchaseOrder, useUpdatePurchaseOrder,
  useApprovePurchaseOrder, useSendPurchaseOrder,
  useReceivePurchaseOrder, useCancelPurchaseOrder,
  useRequestPurchaseOrderApproval, useConvertOrderToInvoice,
  downloadPurchaseOrderPdf,
  type PurchaseOrderListItem, type PurchaseOrderStatus, type POLine,
} from '../hooks/usePurchases'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { PurchaseOrderModal } from './PurchaseOrderModal'
import { PurchaseOrderDetailModal } from './PurchaseOrderDetailModal'
import { RequestApprovalModal } from './RequestApprovalModal'
import { ConvertToInvoiceModal } from './ConvertToInvoiceModal'
import { formatDate, formatNumber, useTenantLocale } from '@/lib/locale'
import { Pagination } from '@/components/ds/Pagination'
import { Modal } from '@/components/ds/Modal'

const STATUS_CONFIG: Record<PurchaseOrderStatus, { label: string; cls: string; dotCls: string }> = {
  draft:     { label: 'Borrador',   cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
  approved:  { label: 'Aprobada',   cls: 'bg-warning-surface text-warning-content',     dotCls: 'bg-warning-solid' },
  sent:      { label: 'Enviada',    cls: 'bg-primary-50 text-primary-hover',       dotCls: 'bg-primary/60' },
  received:  { label: 'Recibida',   cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  cancelled: { label: 'Anulada',    cls: 'bg-surface-muted text-content-muted',    dotCls: 'bg-content-subtle' },
}

function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function CancelModal({ order, onConfirm, onCancel, isPending }: {
  order: PurchaseOrderListItem; onConfirm: (reason: string) => void; onCancel: () => void; isPending: boolean
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      title="Anular Orden de Compra"
      subtitle={`Proveedor: ${order.supplier_name}`}
      actions={
        <>
          <button onClick={onCancel}
            className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">
            Volver
          </button>
          <button onClick={() => onConfirm(reason)} disabled={isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-danger-solid text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-60">
            {isPending && <Loader2 size={13} className="animate-spin" />}
            Anular
          </button>
        </>
      }
    >
      <textarea
        aria-label="Motivo de anulación"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Motivo de anulación (opcional)..."
        rows={2}
        className="w-full px-3 py-2 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </Modal>
  )
}

export function PurchaseOrdersPage() {
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingOrder, setEditingOrder] = useState<PurchaseOrderListItem | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [cancelingOrder, setCancelingOrder] = useState<PurchaseOrderListItem | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const { can } = usePermission()
  const canCreate = can('purchases.orders', 'create')
  const canEdit = can('purchases.orders', 'edit')
  const canDelete = can('purchases.orders', 'delete')
  const canApprove = can('purchases.orders', 'approve')
  const canCreateInvoice = can('purchases.invoices', 'create')

  const [requestForOrder, setRequestForOrder] = useState<PurchaseOrderListItem | null>(null)
  const [convertForOrder, setConvertForOrder] = useState<PurchaseOrderListItem | null>(null)
  const requestApproval = useRequestPurchaseOrderApproval()
  const convertToInvoice = useConvertOrderToInvoice()

  const { data, isLoading } = usePurchaseOrders({
    status: statusFilter || undefined,
    page,
    page_size: 25,
  })

  const createOrder = useCreatePurchaseOrder()
  const updateOrder = useUpdatePurchaseOrder(editingOrder?.id ?? '')
  const approve = useApprovePurchaseOrder()
  const send = useSendPurchaseOrder()
  const receive = useReceivePurchaseOrder()
  const cancel = useCancelPurchaseOrder()

  const locale = useTenantLocale()

  const handleSave = (formData: { supplier: string; lines: POLine[]; currency?: string; notes?: string; payment_terms?: string; expected_date?: string; expense_account?: string | null }) => {
    setApiError(null)
    if (editingOrder) {
      updateOrder.mutate(formData, {
        onSuccess: () => { setModalOpen(false); setEditingOrder(null) },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      })
    } else {
      createOrder.mutate(formData, {
        onSuccess: () => setModalOpen(false),
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setApiError(err.response?.data?.error?.message ?? 'Error al crear')
        },
      })
    }
  }

  const fmtAmount = (v: string, currency = 'CLP') =>
    formatNumber(parseFloat(v) || 0, locale, currency === 'CLP' ? 0 : 2)

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Órdenes de Compra"
        subtitle={`${data?.count ?? 0} orden${data?.count !== 1 ? 'es' : ''}`}
        help={HELP['purchases.orders']}
      >
        {canCreate && (
          <button
            onClick={() => { setEditingOrder(null); setApiError(null); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={15} />Nueva Orden
          </button>
        )}
      </PageHeader>

      {/* Filtro de estado */}
      <div className="flex gap-2 mb-4">
        {(['', 'draft', 'approved', 'sent', 'received', 'cancelled'] as const).map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1) }}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s ? 'bg-primary text-on-primary border-primary' : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (STATUS_CONFIG[s as PurchaseOrderStatus]?.label ?? s) : 'Todas'}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : !data?.orders.length ? (
          <EmptyState icon={ClipboardList} size="md" title="No hay órdenes de compra" />
        ) : (
          <DataTable
            columns={[
              { key: 'order_number', header: 'Nº OC', render: o => <span className="font-mono font-medium text-content">{o.order_number || '—'}</span> },
              { key: 'supplier_name', header: 'Proveedor', render: o => <span className="font-medium text-content">{o.supplier_name}</span> },
              { key: 'order_date', header: 'Fecha', render: o => formatDate(o.order_date, locale) },
              { key: 'status', header: 'Estado', render: o => <StatusBadge status={o.status} /> },
              {
                key: 'total', header: 'Total', numeric: true,
                sortValue: o => Number(o.total),
                render: o => (
                  <span className="font-medium text-content">
                    {o.currency && o.currency !== 'CLP' && (
                      <span className="text-[10px] text-content-subtle mr-1">{o.currency}</span>
                    )}
                    {fmtAmount(o.total, o.currency)}
                  </span>
                ),
              },
              {
                key: 'acciones', header: 'Acciones',
                // El stopPropagation va acá dentro: la fila entera navega al
                // detalle, y sin esto cada botón abriría el modal además de
                // ejecutar su acción.
                render: o => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {o.status === 'draft' && (
                      <>
                        {canApprove && (
                          <button onClick={() => approve.mutate(o.id)}
                            disabled={approve.isPending}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface transition-colors disabled:opacity-60">
                            <CheckCircle size={11} />Aprobar
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={() => { setEditingOrder(o); setApiError(null); setModalOpen(true) }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                            Editar
                          </button>
                        )}
                      </>
                    )}
                    <KebabMenu items={[
                      { label: 'Descargar PDF', icon: <FileText size={14} />, onClick: () => downloadPurchaseOrderPdf(o.id, `${o.order_number || o.id}.pdf`), hidden: o.status === 'cancelled' },
                      { label: o.approver_name ? 'Reasignar aprobador' : 'Solicitar aprobación', icon: <UserCheck size={14} />, onClick: () => setRequestForOrder(o), hidden: o.status !== 'draft' || !canEdit, className: 'text-warning-content hover:bg-warning-surface' },
                      { label: 'Enviar', icon: <Send size={14} />, onClick: () => send.mutate(o.id), hidden: o.status !== 'approved' || !canEdit, className: 'text-primary hover:bg-primary-50', disabled: send.isPending },
                      { label: 'Recibir', icon: <PackageCheck size={14} />, onClick: () => receive.mutate({ id: o.id }), hidden: o.status !== 'sent' || !canEdit, className: 'text-success-content-soft hover:bg-success-surface', disabled: receive.isPending },
                      { label: 'Convertir a factura', icon: <ArrowRight size={14} />, onClick: () => setConvertForOrder(o), hidden: !['approved', 'sent', 'received'].includes(o.status) || o.has_invoice || !canCreateInvoice, className: 'text-primary hover:bg-primary-50' },
                      { label: 'Anular', icon: <XCircle size={14} />, onClick: () => setCancelingOrder(o), hidden: !['draft', 'approved'].includes(o.status) || !canDelete, className: 'text-danger-content-soft hover:bg-danger-surface' },
                    ]} />
                  </div>
                ),
              },
            ]}
            rows={data.orders}
            rowKey={o => o.id}
            onRowClick={o => setDetailId(o.id)}
            // El panel lo pone el contenedor, que envuelve también los otros estados.
            surface={false}
            minWidth={900}
            caption="Órdenes de compra"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modales */}
      {modalOpen && (
        <PurchaseOrderModal
          orderId={editingOrder?.id ?? null}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingOrder(null) }}
          isPending={createOrder.isPending || updateOrder.isPending}
          error={apiError}
        />
      )}
      {detailId && (
        <PurchaseOrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
      )}
      {cancelingOrder && (
        <CancelModal
          order={cancelingOrder}
          onConfirm={reason => {
            cancel.mutate({ id: cancelingOrder.id, reason }, {
              onSuccess: () => setCancelingOrder(null),
            })
          }}
          onCancel={() => setCancelingOrder(null)}
          isPending={cancel.isPending}
        />
      )}
      {requestForOrder && (
        <RequestApprovalModal
          open={!!requestForOrder}
          orderNumber={requestForOrder.order_number}
          onClose={() => setRequestForOrder(null)}
          isPending={requestApproval.isPending}
          onConfirm={(approverId) => {
            requestApproval.mutate({ id: requestForOrder.id, approverId }, {
              onSuccess: () => setRequestForOrder(null),
            })
          }}
        />
      )}
      {convertForOrder && (
        <ConvertToInvoiceModal
          open={!!convertForOrder}
          orderNumber={convertForOrder.order_number}
          onClose={() => setConvertForOrder(null)}
          isPending={convertToInvoice.isPending}
          onConfirm={(data) => {
            convertToInvoice.mutate({ id: convertForOrder.id, ...data }, {
              onSuccess: () => setConvertForOrder(null),
            })
          }}
        />
      )}
    </div>
  )
}
